import React, { useEffect, useState, useMemo } from "react";
import Modal from "./Modal";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import SearchFilter from "../inputs/SearchFilter";
import {
  useLazyListAdminLearningTracksQuery,
  useBulkAddCourseToLearningTracksMutation,
} from "../../services/organization/adminLearningTrack";
import { toast } from "react-toastify";
import AdminLearningPathCard from "../cards/AdminLearningPathCard";
import Checkbox from "../inputs/Checkbox";
import InputSearch from "../inputs/InputSearch";

const MODAL_ID = "learning-paths-modal";

export default function LearningPathsModal({ course, isOpened, onClose }) {
  const { t } = useTranslation("learningPaths");
  const { t: commonT } = useTranslation("common");

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [learningTracks, setLearningTracks] = useState([]);
  const [selectedTrackIds, setSelectedTrackIds] = useState(new Set());

  const [fetchTracks, { data: fetchedTracks, isFetching }] =
    useLazyListAdminLearningTracksQuery();
  const [bulkAddCourseToTracks, { isLoading: isAdding }] =
    useBulkAddCourseToLearningTracksMutation();

  useEffect(() => {
    if (isOpened) {
      setPage(1);
      setSearch("");
      setLearningTracks([]);
      setSelectedTrackIds(new Set());
    } else {
      setLearningTracks([]);
      setPage(1);
    }
  }, [isOpened]);

  useEffect(() => {
    if (isOpened) {
      let filters = search ? `filters[keyword]=${search}` : "";
      if (course) {
        filters += `&filters[course_id]=${course.id}`;
      }
      fetchTracks({ page, filters });
    }
  }, [page, search, isOpened]);

  useEffect(() => {
    if (fetchedTracks?.data && isOpened) {
      if (page === 1) {
        setLearningTracks(fetchedTracks.data);
      } else {
        setLearningTracks((prev) => {
          const existingIds = new Set(prev.map((t) => t.id));
          const newTracks = fetchedTracks.data.filter(
            (t) => !existingIds.has(t.id),
          );
          return [...prev, ...newTracks];
        });
      }
    }
  }, [fetchedTracks, page, isOpened]);

  const toggleTrack = (id) => {
    setSelectedTrackIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleAddCourse = async () => {
    if (selectedTrackIds.size === 0 || !course) return;

    try {
      await bulkAddCourseToTracks({
        course_id: course.id,
        learning_track_ids: Array.from(selectedTrackIds),
      }).unwrap();

      toast.success(t("courseAddedToTrackSuccess"));
      onClose();
    } catch (error) {
      console.error("Failed to add course to track(s):", error);
      toast.error(t("failedToAddCourseToTrack"));
    }
  };

  useEffect(() => {
    const el = document.getElementById(MODAL_ID);
    if (!el) return;
    const bootstrapModal = window.bootstrap?.Modal?.getOrCreateInstance(el);
    if (!bootstrapModal) return;
    if (isOpened) {
      bootstrapModal.show();
    } else {
      bootstrapModal.hide();
    }
  }, [isOpened]);

  useEffect(() => {
    const el = document.getElementById(MODAL_ID);
    if (!el) return;
    const handleHidden = () => onClose?.();
    el.addEventListener("hidden.bs.modal", handleHidden);
    return () => el.removeEventListener("hidden.bs.modal", handleHidden);
  }, [onClose]);

  return (
    <Modal
      id={MODAL_ID}
      title={t("addToLearningTrack")}
      hasCloseBtn
      isOpened={isOpened}
      onClose={onClose}
      maxWidth={800}
      isVerticallyCentered
      className="add-to-learning-path-modal modal-full-height"
    >
      <div className="add-learning-path-modal-wrapper">
        <div className="mb-3">
          <InputSearch
            placeHolder={commonT("search")}
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
          />
        </div>

        <div id="scrollableDiv" className="scrollable-infinite-modal">
          <InfiniteScroll
            dataLength={learningTracks.length}
            next={() => setPage((prev) => prev + 1)}
            hasMore={
              learningTracks.length > 0 && !!fetchedTracks?.pagination?.next
            }
            loader={<h4 className="text-center mt-3">{commonT("Loading")}</h4>}
            scrollableTarget="scrollableDiv"
          >
            {learningTracks.map((track) => (
              <div
                key={track.id}
                className={`add-to-learning-path-modal-item ${selectedTrackIds.has(track.id) ? "selected" : ""}`}
                onClick={() => toggleTrack(track.id)}
              >
                <div className="add-to-learning-path-modal-item__check">
                  <Checkbox checked={selectedTrackIds.has(track.id)} />
                </div>
                <div className="add-to-learning-path-modal-item__card">
                  <AdminLearningPathCard
                    track={track}
                    disableNavigation={true}
                  />
                </div>
              </div>
            ))}
            {!isFetching && learningTracks.length === 0 && (
              <p className="text-center text-muted mt-4">
                {t("noTracksFound")}
              </p>
            )}
          </InfiniteScroll>
        </div>

        <div className="sticky-footer-action">
          <button
            type="button"
            className="btn btn-main-color w-100"
            onClick={handleAddCourse}
            disabled={selectedTrackIds.size === 0 || isAdding}
          >
            {isAdding ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {commonT("saving")}...
              </>
            ) : (
              commonT("add")
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
